import { Component, Element, Prop, h, Host, Event, EventEmitter, State, Listen, Watch } from '@stencil/core';
import { generateId, focusElement, runAfterNextFrameRender, isDescendant } from '../../utils/functions';
import { getInternalFormat, isDateString, getInternalFormatFromDate, getDateFromInternalFormat, formatDate, isValidDate } from '../../utils/dates';
import { breakpoints, KEY_CODES } from '../../utils/variables';

@Component({
  tag: 'som-date-range-picker-button',
  styleUrl: 'som-date-range-picker-button.scss',
})
export class SomDateRangePickerButton {
  @Element() el: HTMLElement;

  resizeTimeout;

  updateResponsivenessCb;

  buttonEl: HTMLSomButtonElement;

  popoverEl: HTMLSomPopoverElement;

  mobileDateRangePickerEl: HTMLSomDatePickerMobileElement;

  popoverDateRangePickerEl: HTMLSomDateRangePickerElement;

  input1El: HTMLInputElement;

  input2El: HTMLInputElement;

  popoverId = generateId('som-date-range-picker-popover');

  buttonId = generateId('som-date-range-picker-button');

  enableInputFocus = true;

  // Debounce timeout used to prevent flickering when focus is on input and user focuses out of the browser body content (ex.: URL bar) and clicking back inside the page.
  focusBlurDebounceTimeout;

  /**
   * This property is used to set the date picker as disabled.
   */
  @Prop() disabled = false;

  /**
   * This property can be used to set the ARIA label associated with this input (for screen readers).
   */
  @Prop() buttonAriaLabel = '';

  /**
   * This property is the form element name (used when submitting the form).
   */
  @Prop() name = '';

  /**
   * This property determines the color of the button.
   */
  @Prop() buttonColor: 'primary' | 'secondary' | 'white' | 'nav' | 'gray' | 'danger' = 'gray';  

  /**
   * This property determines the size of the button.
   */
  @Prop() buttonSize?: 'sm' | 'md' | 'lg' | 'block' = 'md';  

  /**
   * This property determines which side of the button the icon should appear.
   */
  @Prop() buttonIconPosition?: 'left' | 'right' | 'only' | 'none' = 'none';

  /**
   * This property determines which icon to be placed.  It should be used in conjunction with iconPosition.
   */
  @Prop() buttonIcon?: string;

  /**
   * This property determines the button style.
   */
  @Prop() buttonType: 'default' | 'outline' | 'clear' = 'default';

  /**
   * This property determines the initial label of the button. Once a date range is selected, the label will switch to the selected values.
   */
  @Prop() buttonLabel: string = 'Select Date Range';

  /**
   * This property is used to toggle the loading mode.
   */
  @Prop() loading?: boolean = false;  

  /**
   * This property sets the horizontal position of the date picker popover.
   */
  @Prop() horizontalPosition: 'left' | 'right' = 'left';

  /**
   * This property sets the vertical position of the date picker popover.
   */
  @Prop() verticalPosition: 'top' | 'bottom' = 'bottom';

  /**
   * This property sets the date format.
   */
  @Prop() format: 'MM-DD-YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'YYYY/MM/DD' = 'MM/DD/YYYY';

  /**
   * This property sets the start date.
   */
  @Prop({ mutable: true, reflect: true }) startDate = '';

  @Watch('startDate')
  handleStartDateChange() {
    this.updateStartDate();
  }

  /**
   * This property sets the end date.
   */
  @Prop({ mutable: true, reflect: true }) endDate = '';

  @Watch('endDate')
  handleEndDateChange() {
    this.updateEndDate();
  }

  @State() isMobile = false;

  @State() date1 = '';

  @State() date2 = '';

  @State() date1Tmp;

  @State() date2Tmp;

  /**
   * This event is emitted when the date range changes.
   */
  @Event() valueChange: EventEmitter;

  @Listen('blur', { target: 'window', capture: true })
  handleWindowBlur(e) {
    if (this.buttonEl === document.activeElement) {
      // Focus is removed from the page (i.e. user views another browser tab). We don't want to trigger an input focus.
      e.stopPropagation();
      e.preventDefault();
    }
  }

  @Listen('focus', { target: 'window', capture: true })
  handleWindowFocus(e) {
    if (this.buttonEl === document.activeElement) {
      // Focus is given to the page (i.e. user comes back from another browser tab). We don't want to trigger an input focus.
      e.stopPropagation();
      e.preventDefault();
    }
  }

  @Listen('resize', { target: 'window' })
  handleWindowResize() {
    clearTimeout(this.resizeTimeout);

    // Setup debounce so we're not constantly reading screen width
    this.resizeTimeout = setTimeout(this.updateResponsivenessCb, 300);
  }

  @Listen('keydown')
  async handleKeyDown(e) {
    if (e.keyCode === KEY_CODES.ESC) {
      // For both mobile and desktop
      e.preventDefault();
      this.focusSilently();
    } else if (e.keyCode === KEY_CODES.ARROW_DOWN && this.isDateRangePickerDisplayed()) {
      if (this.popoverDateRangePickerEl) {
        // Execute this code if calendar is not already shown
        e.preventDefault();
        this.showDatePicker(true);

        this.popoverDateRangePickerEl.setFocus();
      }
    }
  }

  componentWillLoad() {
    this.updateResponsivenessCb = this.updateResponsiveness.bind(this);

    this.updateResponsiveness();

    this.updateStartDate();
    this.updateEndDate();
  }

  componentDidLoad() {
    this.buttonEl = this.el.querySelector(`#${this.buttonId}`);
  }

  get dateRange() {
    if (!this.date1) {
      return null;
    }

    let dateRange = [<div>{formatDate(getDateFromInternalFormat(this.date1), this.format)}</div>];

    if (this.date2) {
      dateRange = dateRange.concat([
        <div class="som-drp-button-arrow">
          <som-icon name="arrow-right" size="sm" display="block"></som-icon>
        </div>,
        <div>{formatDate(getDateFromInternalFormat(this.date2), this.format)}</div>,
      ]);
    }

    return dateRange;
  }

  updateStartDate() {
    this.date1 = this.startDate && isDateString(this.startDate, this.format) ? getInternalFormat(this.startDate, this.format) : '';
    this.date1Tmp = this.date1;
  }

  updateEndDate() {
    this.date2 = this.endDate && isDateString(this.endDate, this.format) ? getInternalFormat(this.endDate, this.format) : '';
    this.date2Tmp = this.date2;
  }

  updateResponsiveness() {
    this.isMobile = window.innerWidth < breakpoints.md;
  }

  async focusSilently() {
    // We put focus on the input
    this.enableInputFocus = false;
    await focusElement(this.buttonEl);

    // For IE -- Put the re-enable on the top of the stack. This prevents the 'focus' event to be triggered right after the date picker is closed (which would reopen it right away).
    setTimeout(() => {
      this.enableInputFocus = true;
    }, 0);
  }

  handleButtonBlur() {
    const el = this.popoverEl || this.mobileDateRangePickerEl;
    if (el === this.popoverEl) {
      clearTimeout(this.focusBlurDebounceTimeout);
      this.focusBlurDebounceTimeout = setTimeout(() => {
        const hasFocus = isDescendant(el, document.activeElement);
        if (!hasFocus) {
          this.hideDatePicker();
        }
      }, 100);
    }
  }

  isDateRangePickerDisplayed() {
    if (this.popoverEl) {
      if (this.popoverEl.hasAttribute('show') && this.popoverEl.getAttribute('show') !== 'false') {
        return true;
      }
    } else if (this.mobileDateRangePickerEl) {
      if (this.mobileDateRangePickerEl.hasAttribute('show') && this.mobileDateRangePickerEl.getAttribute('show') !== 'false') {
        return true;
      }
    }

    return false;
  }

  handleButtonClick() {
    if (!this.isDateRangePickerDisplayed()) {
      this.showDatePicker();
    }
  }

  handleInput1Text(e) {
    this.date1Tmp = isDateString(e.target.value, this.format) ? getInternalFormat(e.target.value, this.format) : '';
    this.popoverDateRangePickerEl.setAttribute('start-date', this.date1Tmp);

    runAfterNextFrameRender(() => {
      (this.mobileDateRangePickerEl || this.popoverDateRangePickerEl).reset();
    });
  }

  handleInput2Text(e) {
    this.date2Tmp = isDateString(e.target.value, this.format) ? getInternalFormat(e.target.value, this.format) : '';
    this.popoverDateRangePickerEl.setAttribute('end-date', this.date2Tmp);

    runAfterNextFrameRender(() => {
      (this.mobileDateRangePickerEl || this.popoverDateRangePickerEl).reset();
    });
  }

  handleDatesSelected(e) {
    e.stopPropagation();
    const {
      startDate,
      endDate,
    }: {
      startDate: null | Date;
      endDate: null | Date;
    } = e.detail;

    this.input1El.value = formatDate(startDate, this.format);
    this.date1Tmp = getInternalFormatFromDate(startDate);

    this.input2El.value = endDate ? formatDate(endDate, this.format) : '';
    this.date2Tmp = endDate ? getInternalFormatFromDate(endDate) : '';
  }

  handleApplySelectedDates() {
    this.focusSilently();
    this.hideDatePicker();

    this.startDate = this.date1Tmp ? formatDate(getDateFromInternalFormat(this.date1Tmp), this.format) : '';
    this.endDate = this.date2Tmp ? formatDate(getDateFromInternalFormat(this.date2Tmp), this.format) : '';
  }

  handleClearSelectedDates() {
    this.date1Tmp = '';
    this.date2Tmp = '';

    if (this.mobileDateRangePickerEl) {
      this.mobileDateRangePickerEl.setAttribute('date1', '');
      this.mobileDateRangePickerEl.setAttribute('date2', '');
    } else if (this.popoverDateRangePickerEl) {
      this.popoverDateRangePickerEl.setAttribute('start-date', '');
      this.popoverDateRangePickerEl.setAttribute('end-date', '');
      this.input1El.value = '';
      this.input2El.value = '';
    }
  }

  handleMobileDateChange(e) {
    this.focusSilently();
    this.hideDatePicker();

    const {
      date1,
      date2,
    }: {
      date1: null | Date;
      date2: null | Date;
    } = e.detail;

    this.startDate = date1 ? formatDate(date1, this.format) : '';
    this.endDate = date2 ? formatDate(date2, this.format) : '';
  }

  async showDatePicker(focusOnShow = false) {
    // Wait for date picker to reset before showing it
    await this.resetDatePicker();

    if (this.mobileDateRangePickerEl) {
      this.mobileDateRangePickerEl.setAttribute('focus-input-on-show', focusOnShow ? 'true' : 'false');
      this.mobileDateRangePickerEl.setAttribute('show', 'true');
    } else {
      this.popoverEl.setAttribute('show', 'true');
    }
  }

  async resetDatePicker() {
    this.updateStartDate();
    this.updateEndDate();

    if (this.mobileDateRangePickerEl) {
      this.mobileDateRangePickerEl.setAttribute('date1', this.date1);
      this.mobileDateRangePickerEl.setAttribute('date2', this.date2);
    } else if (this.popoverDateRangePickerEl) {
      this.popoverDateRangePickerEl.setAttribute('start-date', this.date1);
      this.popoverDateRangePickerEl.setAttribute('end-date', this.date2);
      this.input1El.value = this.date1 && isValidDate(this.date1) ? formatDate(getDateFromInternalFormat(this.date1), this.format) : '';
      this.input2El.value = this.date2 && isValidDate(this.date2) ? formatDate(getDateFromInternalFormat(this.date2), this.format) : '';
    }

    const dateRangePickerEl = this.mobileDateRangePickerEl || this.popoverDateRangePickerEl;
    return dateRangePickerEl.reset();
  }

  hideDatePicker() {
    const el = this.mobileDateRangePickerEl || this.popoverEl;
    el.removeAttribute('show');
  }

  handlePopoverClose() {
    // Popover closed
  }

  handleMobileClose() {
    this.focusSilently();
  }

  render() {
    const ariaDisabled = this.disabled ? { 'aria-disabled': '' } : null;

    return (
      <Host>
        <som-button
          id={this.buttonId}
          type="button"
          color={this.buttonColor}
          size={this.buttonSize}
          iconPosition={this.buttonIconPosition}
          icon={this.buttonIcon}
          buttonType={this.buttonType}
          loading={this.loading}
          disabled={this.disabled}
          aria-label={`${this.buttonAriaLabel} ${this.dateRange}`}
          aria-controls={this.popoverId}
          onClick={this.handleButtonClick.bind(this)}
          onBlur={this.handleButtonBlur.bind(this)}
          {...ariaDisabled}
        >
          {this.dateRange || this.buttonLabel}
        </som-button>
        {this.isMobile ? (
          <som-date-picker-mobile
            ref={ref => {
              this.mobileDateRangePickerEl = ref;
            }}
            date1={this.date1}
            date2={this.date2}
            format={this.format}
            enableRangePicker={true}
            onClose={this.handleMobileClose.bind(this)}
            onDatesChanged={this.handleMobileDateChange.bind(this)}
          />
        ) : (
          <som-popover
            ref={ref => {
              this.popoverEl = ref;
            }}
            id={this.popoverId}
            for={this.buttonId}
            vertical-position={this.verticalPosition}
            horizontal-position={this.horizontalPosition}
            relative-positioning
            onPopoverClosed={this.handlePopoverClose.bind(this)}
          >
            <div class="som-drp-inputs-container">
              <input
                ref={ref => {
                  this.input1El = ref;
                }}
                type="text"
                class="form-control"
                value={this.startDate}
                onInput={this.handleInput1Text.bind(this)}
                placeholder={this.format}
                aria-label="Start date"
              />

              <div class="som-dpm-input-arrow">
                <som-icon name="caret-right"></som-icon>
              </div>

              <input
                ref={ref => {
                  this.input2El = ref;
                }}
                type="text"
                class="form-control"
                value={this.endDate}
                onInput={this.handleInput2Text.bind(this)}
                placeholder={this.format}
                aria-label="End date"
              />
            </div>
            <div class="som-drp-popover-content">
              <som-date-range-picker
                ref={ref => {
                  this.popoverDateRangePickerEl = ref;
                }}
                startDate={this.date1}
                endDate={this.date2}
                onDatesChanged={this.handleDatesSelected.bind(this)}
              ></som-date-range-picker>
            </div>
            <div class="som-drp-button-actions">
              <button class="som-drp-button-actions__clear" onClick={this.handleClearSelectedDates.bind(this)}>Clear</button>
              <button class="som-drp-button-actions__apply" onClick={this.handleApplySelectedDates.bind(this)}>Apply</button>
            </div>
          </som-popover>
        )}
      </Host>
    );
  }
}
